/**
 * Даты и числа по-русски. Время везде показываем в одном поясе — из настроек платформы
 * (центр в Лангепасе, Asia/Yekaterinburg), а не в поясе браузера. До загрузки настроек — пояс браузера.
 */
import { useSyncExternalStore } from "react";

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

let zone = browserZone();
const listeners = new Set<() => void>();

function makeFormatters(tz: string) {
  const dtf = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("ru-RU", { ...o, timeZone: tz });
  return {
    dayMonth: dtf({ day: "numeric", month: "long" }),
    dayMonthYear: dtf({ day: "numeric", month: "long", year: "numeric" }),
    time: dtf({ hour: "2-digit", minute: "2-digit" }),
    weekday: dtf({ weekday: "long" }),
    weekdayShort: dtf({ weekday: "short" }),
    // числа даты и времени в поясе — для ключей дней и поля datetime-local
    parts: new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }),
  };
}
let fmt = makeFormatters(zone);

export const displayTimeZone = () => zone;

/** Сменить пояс показа (из настроек). Пусто — пояс браузера, неизвестный пояс — без изменений. */
export function setDisplayTimeZone(tz: string | null | undefined): boolean {
  const next = tz?.trim() || browserZone();
  if (next === zone) return false;
  let made: ReturnType<typeof makeFormatters>;
  try {
    made = makeFormatters(next);
  } catch {
    return false;
  }
  zone = next;
  fmt = made;
  for (const l of listeners) l();
  return true;
}

export function subscribeDisplayTimeZone(cb: () => void) {
  listeners.add(cb);
  return () => void listeners.delete(cb);
}

/** Пояс показа для компонента: перерисует его, когда пояс сменится. */
export const useDisplayTimeZone = () => useSyncExternalStore(subscribeDisplayTimeZone, displayTimeZone, displayTimeZone);

/* ---- календарные числа в поясе показа ---- */

const pad = (n: number) => String(n).padStart(2, "0");

function partsOf(ms: number) {
  const p: Record<string, string> = {};
  for (const x of fmt.parts.formatToParts(new Date(ms))) p[x.type] = x.value;
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
}

/** Смещение пояса показа (мс) в момент ms: местное = UTC + смещение. */
function offsetAt(ms: number) {
  const p = partsOf(ms);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

/** Местные дата и время в поясе показа → момент (мс). Несуществующее (перевод часов вперёд) — сдвигаем вперёд, двойное — первое. */
export function zonedToMs(y: number, m: number, d: number, h = 0, mi = 0): number {
  const wall = Date.UTC(y, m - 1, d, h, mi);
  const before = offsetAt(wall - 864e5);
  const after = offsetAt(wall + 864e5);
  const fits = [before, after].map((o) => wall - o).filter((u) => offsetAt(u) === wall - u);
  return fits.length ? Math.min(...fits) : wall - before;
}

const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const splitKey = (key: string) => key.split("-").map(Number) as [number, number, number];

/** «2026-10-22» — день момента ms в поясе показа. */
export function dayKey(ms: number): string {
  const p = partsOf(ms);
  return keyOf(p.y, p.m, p.d);
}

/** Сдвиг календарного дня «YYYY-MM-DD» на n дней. */
export function addDaysKey(key: string, n: number): string {
  const [y, m, d] = splitKey(key);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return keyOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Сколько календарных дней от from до to. */
export function daysBetween(from: string, to: string): number {
  const [y1, m1, d1] = splitKey(from);
  const [y2, m2, d2] = splitKey(to);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 864e5);
}

/** Полдень дня «YYYY-MM-DD» в поясе показа — для подписей дня. */
export function dayNoon(key: string): number {
  const [y, m, d] = splitKey(key);
  return zonedToMs(y, m, d, 12, 0);
}

export const toMs = (sec: number) => sec * 1000;

export function sameDay(a: Date | number, b: Date | number) {
  return dayKey(+a) === dayKey(+b);
}

/** «22 октября, 10:00» (год — если не текущий) */
export function dateTime(ms: number | null | undefined): string {
  if (!ms) return "";
  const d = new Date(ms);
  const day = partsOf(ms).y === partsOf(Date.now()).y ? fmt.dayMonth.format(d) : fmt.dayMonthYear.format(d);
  return `${day}, ${fmt.time.format(d)}`;
}

/** «сегодня, 10:00» / «завтра, 10:00» / «22 октября, 10:00» */
export function humanDateTime(ms: number | null | undefined): string {
  if (!ms) return "";
  const key = dayKey(ms);
  const today = dayKey(Date.now());
  const t = fmt.time.format(new Date(ms));
  if (key === today) return `сегодня, ${t}`;
  if (key === addDaysKey(today, 1)) return `завтра, ${t}`;
  if (key === addDaysKey(today, -1)) return `вчера, ${t}`;
  return dateTime(ms);
}

export const time = (ms: number) => fmt.time.format(new Date(ms));
export const dayMonth = (ms: number) => fmt.dayMonth.format(new Date(ms));
export const weekday = (ms: number) => fmt.weekday.format(new Date(ms));
export const weekdayShort = (ms: number) => fmt.weekdayShort.format(new Date(ms));

/** «3 мин назад» */
export function ago(ms: number | null | undefined): string {
  if (!ms) return "";
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 45) return "только что";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин назад`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч назад`;
  return humanDateTime(ms);
}

export function duration(ms: number | null | undefined): string {
  if (ms == null) return "";
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} с`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} мин ${r} с` : `${m} мин`;
}

export function plural(n: number, one: string, few: string, many: string) {
  const a = Math.abs(n) % 10;
  const b = Math.abs(n) % 100;
  if (a === 1 && b !== 11) return one;
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return few;
  return many;
}

export const countText = (n: number, one: string, few: string, many: string) => `${n} ${plural(n, one, few, many)}`;

/** Значение для <input type="datetime-local"> — в поясе показа. */
export function toLocalInput(d: Date) {
  const p = partsOf(d.getTime());
  return `${keyOf(p.y, p.m, p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** «YYYY-MM-DDTHH:mm» из datetime-local (время в поясе показа) → unix-секунды; неверное значение — NaN. */
export function fromLocalInput(value: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value.trim());
  if (!m) return NaN;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return NaN;
  return Math.floor(zonedToMs(y, mo, d, h, mi) / 1000);
}

/** Через 30 минут, с округлением вверх до 5 минут (в поясе показа) — время по умолчанию для «В отложенные». */
export function postponeSlot(now = Date.now()): string {
  const step = 5 * 60_000;
  return toLocalInput(new Date(Math.ceil((now + 30 * 60_000) / step) * step));
}

export const domainOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
